import { REDIRECT_URI, REDIRECT_PATH } from '@common/Utility/constants';
import { EVENT_NAMES, getPageName, getRegionName } from '@common/Constants/analytics';
import { dataLayerSendEvent as sendGtmEventData } from '@common/Utility/analytics-utils';
import { getDataFromCookie } from 'aster-foundation/core/common/utils/cookieHelper';
import { DEFAULT_HEADER_VALUES } from 'aster-foundation/core/common/utils/ApiConst';

const dataLayerSendEvent = (eventName, data) => {
  sendGtmEventData(eventName, data);
};

// TODO: use this file for new login/register events or for complex events
export const getRefererPage = () => {
  try {
    const url = window?.location?.href;
    const decodeUrl = new URL(new URL(url)?.searchParams?.get('redirectUri'));
    sessionStorage.setItem(REDIRECT_URI, decodeUrl);
    let redirectPath = decodeUrl?.searchParams?.get('asPath');
    if (!redirectPath) redirectPath = decodeUrl?.pathname;
    sessionStorage.setItem(REDIRECT_PATH, redirectPath);
    return getPageName(redirectPath);
  } catch (e) {
    return '';
  }
};

const getCommonEventParams = ({ screenName, pageType, category, referrerPage}) => ({
  screen_name: screenName,
  myaster_category: category,
  page_type: pageType,
  referrer_page: getRefererPage() || referrerPage?.toLowerCase(),
});

const getPageViewEvent = (params) => getCommonEventParams(params);

const getContinueGuestEvent = (params) => getCommonEventParams(params);

const getTempPasswordSendEvent = (params) => getCommonEventParams(params);

const getCreatePasswordEvent = (params) => getCommonEventParams(params);

const getResetPasswordEvent = (params) => getCommonEventParams(params);

const getResetPasswordLinkEvent = (params) => getCommonEventParams(params);

const getForgotPasswordEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  if(params?.sop_type){
    commonParams.sop_type = params.sop_type;
  }
  if(params?.type){
    commonParams.type = params.type;
  }
  return commonParams;
};

const getCompleteProfileEvent = (params) => getCommonEventParams(params);

const getMobileNumberEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  if (params?.initiationSource) {
    commonParams.initiation_source = getRefererPage() || params?.initiationSource?.toLowerCase();
  }
  if (params?.countryCode) {
    commonParams.country_code = params.countryCode;
  }
  if (params?.error) {
    commonParams.error = params?.error;
  }
  if (params?.err_type) {
    commonParams.err_type = params?.err_type;
  }
  if (params?.type) {
    commonParams.type = params?.type;
  }
  if (params?.sop_type) {
    commonParams.sop_type = params?.sop_type;
  }
  return commonParams;
};

const getVerificationAttemptEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  if ( params?.type ) {
    commonParams.type = params?.type;
  }
  if( params?.country_code ) {
    commonParams.country_code = params?.country_code;
  }
  if ( params?.error ) {
    commonParams.error = params?.error;
  }
  if (params?.err_type) {
    commonParams.err_type = params?.err_type;
  }
  return commonParams;
};

const getUaePassBtnClickEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  const { referrer_page, ...filteredParams } = commonParams;
  if(params?.initiationSource) {
    filteredParams.initiation_source = getRefererPage() || params?.initiationSource?.toLowerCase();
  }
  return filteredParams;
};

const getLoginFailEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  if (params?.initiationSource) {
    commonParams.initiation_source = getRefererPage() || params?.initiationSource?.toLowerCase();
  }
  if (params?.error) {
    commonParams.error = params?.error;
  }
};

const getCountrySelectionEvent = (params) => {
  const commonParams = getCommonEventParams(params);
  const region = getDataFromCookie('regionCode');
  return {
    ...commonParams,
    option: getRegionName(region),
    content_language: params?.content_language || DEFAULT_HEADER_VALUES.langCode,
    selected_country: params?.selected_country || DEFAULT_HEADER_VALUES.regionCode
  }
}

const getEventParams = (event, params) => {
  let eventParams = {};
  switch (event) {
    case EVENT_NAMES.PAGE_VIEW:
      eventParams = getPageViewEvent(params);
      break;
    case EVENT_NAMES.CONTINUE_GUEST:
      eventParams = getContinueGuestEvent(params);
      break;
    case EVENT_NAMES.MOBILE_NUMBER_ADD:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.MOBILE_ERROR:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.OTP_SENT:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.RESEND_OTP:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.LOGIN_WITH_EMAIL:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.LOGIN_EMAIL_ERROR:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.TEMP_PASSWORD_SEND:
      eventParams = getTempPasswordSendEvent(params);
      break;
    case EVENT_NAMES.CREATE_PASSWORD:
      eventParams = getCreatePasswordEvent(params);
      break;
    case EVENT_NAMES.PASSWORD_UPDATE:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.VERIFY_OTP_ERROR:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.VERIFY_OTP_SUCCESS:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.RESET_PASSWORD:
      eventParams = getResetPasswordEvent(params);
      break;
    case EVENT_NAMES.RESET_PASSWORD_LINK:
      eventParams = getResetPasswordLinkEvent(params);
      break;
    case EVENT_NAMES.FORGOT_PASSWORD:
      eventParams = getForgotPasswordEvent(params);
      break;
    case EVENT_NAMES.RESET_PASSWORD_ERROR:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.PASSWORD_ERROR:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.BASIC_REGISTRATION_INITIATION:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.BASIC_REGISTRATION_SUCCESS:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.BASIC_REGISTRATION_FAIL:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.VERIFICATION_ATTEMPT_LIMIT_REACHED:
      eventParams = getVerificationAttemptEvent(params);
      break;
    case EVENT_NAMES.LOGIN_SUCCESS:
      eventParams = getMobileNumberEvent(params);
      break;
    case EVENT_NAMES.COMPLETE_PROFILE_INITIATION:
      eventParams = getCompleteProfileEvent(params);
      break;
    case EVENT_NAMES.COMPLETE_PROFILE_SUCCESS:
      eventParams = getCompleteProfileEvent(params);
      break;
    case EVENT_NAMES.UAE_PASS_BUTTON_CLICK:
      eventParams = getUaePassBtnClickEvent(params);
      break;
    case EVENT_NAMES.LOGIN_FAIL:
      eventParams = getLoginFailEvent(params);

    case EVENT_NAMES.COUNTRY_SELECTION:
      eventParams = getCountrySelectionEvent(params);
      break;

    default:
      eventParams = null;
  }
  return eventParams;
};

// eslint-disable-next-line import/prefer-default-export
export const sendEventData = ({ event, ...rest }) => {
  const eventParams = getEventParams(event, rest);
  dataLayerSendEvent(event, eventParams);
};
